import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Ruler, ChevronRight } from "lucide-react";
import api, { inr } from "../lib/api";

const MEASURE_FIELDS = ["Bust", "Waist", "Hip", "Shoulder", "Length", "Sleeve"];
const EMPTY_FORM = { name: "", phone: "", email: "", notes: "", measurements: {} };

export default function PosCustomers() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/pos/customers", { params: { q: query } }).then((r) => setList(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [query]);

  const save = async () => {
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    try {
      if (editing) await api.put(`/pos/customers/${editing}`, form);
      else await api.post("/pos/customers", form);
      toast.success("Saved");
      setForm(EMPTY_FORM);
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    await api.delete(`/pos/customers/${id}`);
    load();
  };

  const settle = async (c) => {
    const amt = window.prompt(`Settle how much of the ${inr(c.balance_due)} balance due for ${c.name}?`, c.balance_due);
    if (amt === null) return;
    const n = parseInt(amt, 10);
    if (!n || n <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/pos/customers/${c.id}/settle`, { amount: n, mode: "cash" });
      toast.success(`${inr(n)} settled`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Settle failed"); }
  };

  return (
    <div className="p-4 sm:p-8" data-testid="pos-customers-page">
      <h1 className="text-2xl font-semibold text-slate-100 mb-1">Customers</h1>
      <p className="text-sm text-slate-500 mb-6">Walk-in customer database with purchase history — use for recall, exchanges, promos.</p>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or phone" className="w-full bg-slate-900 border border-slate-800 rounded pl-10 pr-4 py-3 text-sm text-slate-200" data-testid="pos-cust-search" />
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-md">
            {list.length === 0 && <p className="p-8 text-center text-slate-600 text-sm">No customers yet</p>}
            {list.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-800 last:border-0">
                <button onClick={() => navigate(`/pos/customers/${c.id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left group" data-testid={`cust-open-${c.id}`}>
                  <div className="grid place-items-center h-10 w-10 bg-slate-800 text-amber-400 rounded-full font-medium shrink-0">
                    {c.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-100 truncate group-hover:text-amber-400 transition-colors">{c.name}</p>
                    <p className="text-xs text-slate-500 font-mono truncate">{c.phone}{c.email && ` · ${c.email}`}</p>
                  </div>
                </button>
                <div className="text-right hidden md:block">
                  <p className="text-xs text-slate-500">Spent</p>
                  <p className="text-sm text-amber-400 font-mono">{inr(c.total_spent || 0)}</p>
                </div>
                <div className="text-right hidden md:block">
                  <p className="text-xs text-slate-500">Visits</p>
                  <p className="text-sm text-slate-300 font-mono">{c.visits || 0}</p>
                </div>
                {c.balance_due > 0 && (
                  <div className="text-right hidden md:block" data-testid={`cust-due-${c.id}`}>
                    <p className="text-xs text-slate-500">Due</p>
                    <p className="text-sm text-red-400 font-mono">{inr(c.balance_due)}</p>
                  </div>
                )}
                <div className="flex items-center gap-1 ml-auto">
                  {c.balance_due > 0 && (
                    <button onClick={() => settle(c)} className="text-xs px-2.5 py-1.5 rounded bg-amber-500 text-slate-950 font-semibold hover:bg-amber-400" data-testid={`cust-settle-${c.id}`}>Settle</button>
                  )}
                  <button onClick={() => { setForm({ name: c.name, phone: c.phone, email: c.email || "", notes: c.notes || "", measurements: c.measurements || {} }); setEditing(c.id); }} className="text-xs text-slate-400 hover:text-amber-400 px-2">Edit</button>
                  <button onClick={() => del(c.id)} className="text-xs text-slate-500 hover:text-red-400 px-2">Delete</button>
                  <button onClick={() => navigate(`/pos/customers/${c.id}`)} className="text-slate-400 hover:text-amber-400 p-2" title="Open profile" data-testid={`cust-profile-${c.id}`}><ChevronRight size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="bg-slate-900 border border-slate-800 rounded-md p-5 h-fit sticky top-6">
          <h3 className="text-sm font-medium text-slate-200 mb-4 flex items-center gap-2"><Plus size={14} /> {editing ? "Edit customer" : "Add customer"}</h3>
          <div className="space-y-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200" data-testid="cust-name" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 font-mono" data-testid="cust-phone" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200" />
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (size, preferences, tailor details…)" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 resize-none" />
            <div className="pt-1">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Ruler size={12} /> Measurements (inches)</p>
              <div className="grid grid-cols-3 gap-2">
                {MEASURE_FIELDS.map((m) => (
                  <input
                    key={m}
                    value={form.measurements?.[m] || ""}
                    onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, [m]: e.target.value } })}
                    placeholder={m}
                    className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-2 text-xs text-slate-200 font-mono"
                    data-testid={`cust-measure-${m.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={save} className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-medium text-sm" data-testid="cust-save">{editing ? "Update" : "Add customer"}</button>
              {editing && <button onClick={() => { setEditing(null); setForm(EMPTY_FORM); }} className="px-3 py-2.5 border border-slate-700 rounded text-slate-400 hover:text-slate-100 text-sm">Cancel</button>}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
